import { metrics__get_asker_stats } from 'common/src/api/metrics';
import React, { useState, useEffect } from 'react';

interface MonthlyStats {
  month: number;
  count: number;
  revenue: number;
}

interface AskerStats {
  email: string;
  name: string;
  months: MonthlyStats[];
}

const AskerStatsTable: React.FC = () => {
  const [askerStats, setAskerStats] = useState<AskerStats[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [startDate, setStartDate] = useState(
    new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
  );
  const [endDate, setEndDate] = useState(
    new Date(new Date().getFullYear() + 1, 0, 0).toISOString().split('T')[0],
  );
  const [showRevenue, setShowRevenue] = useState(false);

  const limit = 10; // Number of items per fetch

  useEffect(() => {
    fetchAskerStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const fetchAskerStats = async (loadMore = false) => {

    const { data, error } = await metrics__get_asker_stats(limit, offset, startDate, endDate)
    if (error) {
      console.error('Error fetching asker stats:', error);
    } else if (data) {
      setAskerStats((prevStats) => (loadMore ? [...prevStats, ...data] : data));
      setOffset((prevOffset) => prevOffset + data.length);
      setHasMore(data.length === limit);
    }
  };

  const handleLoadMore = () => {
    fetchAskerStats(true);
  };

  const getMonthsInRange = () => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const months = [];
    for (let d = start; d <= end; d.setMonth(d.getMonth() + 1)) {
      months.push({
        month: d.getMonth() + 1,
        date: new Date(d.getFullYear(), d.getMonth(), 1),
      });
    }
    return months;
  };

  const months = getMonthsInRange();

  const getMonthValue = (asker: AskerStats, month: number) => {
    const stat = asker.months.find((s) => s.month === month);
    if (!stat) return null;
    return showRevenue ? stat.revenue : stat.count;
  };

  const getTotalValue = (asker: AskerStats) => {
    return asker.months.reduce(
      (sum, stat) => sum + (showRevenue ? stat.revenue : stat.count),
      0,
    );
  };

  return (
    <div className="container mx-auto p-4">
      <h2 className="text-2xl font-bold mb-4">Asker Statistics</h2>
      <div className="flex gap-4 mb-4 items-center">
        <div>
          <label
            htmlFor="startDate"
            className="block text-sm font-medium text-gray-700"
          >
            Start Date
          </label>
          <input
            id="startDate"
            type="date"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div>
          <label
            htmlFor="endDate"
            className="block text-sm font-medium text-gray-700"
          >
            End Date
          </label>
          <input
            id="endDate"
            type="date"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        <div className="flex items-center">
          <input
            id="showRevenue"
            type="checkbox"
            className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-offset-0 focus:ring-indigo-200 focus:ring-opacity-50"
            checked={showRevenue}
            onChange={(e) => setShowRevenue(e.target.checked)}
          />
          <label
            htmlFor="showRevenue"
            className="ml-2 block text-sm text-gray-900"
          >
            Show Revenue
          </label>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Email
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Name
              </th>
              {months.map(({ month, date }) => (
                <th
                  key={month}
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {date.toLocaleString('default', {
                    month: 'short',
                    year: 'numeric',
                  })}
                </th>
              ))}
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Total
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {askerStats.map((asker) => (
              <tr key={asker.email}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {asker.email}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {asker.name}
                </td>
                {months.map(({ month }) => {
                  const value = getMonthValue(asker, month);
                  return (
                    <td
                      key={month}
                      className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                    >
                      {value !== null
                        ? showRevenue
                          ? `$${value.toFixed(2)}`
                          : value
                        : '-'}
                    </td>
                  );
                })}
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {showRevenue
                    ? `$${getTotalValue(asker).toFixed(2)}`
                    : getTotalValue(asker)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {hasMore && (
        <div className="mt-4 flex justify-center">
          <button
            onClick={handleLoadMore}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Load More
          </button>
        </div>
      )}
    </div>
  );
};

export default AskerStatsTable;
